<script>
    import { EventBus, readUrlQuery, resizeOrientationAware } from '../../../common/common';
    import { cartStore } from '../../common/store/cart-store';

    export default {
        name: 'BaseVariations',

        props: {
            product: {
                type: Object,
                required: true,
            },
            initialFabric: {
                type: Object,
                required: false,
            },
            label: String,
            reprintMode: Boolean,
            shopMode: Boolean,
            wishlistMode: Boolean,
            showReprintMessage: Boolean,
        },

        model: {
            prop: 'selectedVariation',
        },

        data() {
            return {
                onlyDeliveryReady: false,
                showDeliveryReadyInfo: false,

                selectedFabric: null,
                selectedModeling: null,
                selectedPhoneModel: null,
                selectedSize: null,
                isMobile: true,

                selectedVariation: null,

                loadingCart: false,

                sizeError: null,
            };
        },

        created() {
            this.onlyDeliveryReady =
                this.shopMode &&
                readUrlQuery('inPhysicalStock') === '1' &&
                this.hasDeliveryReadySizes;
        },

        watch: {
            initialFabric: {
                immediate: true,
                handler(to) {
                    if (to) {
                        this.setFabric(this.initialFabric);
                    }
                },
            },
        },

        computed: {
            deadlineAlert() {
                return readUrlQuery('inPhysicalStock') === '1' && !this.onlyDeliveryReady;
            },

            isPhone() {
                return (this.product.type || {}).id === 15;
            },

            showSeparator() {
                return (
                    this.shopMode &&
                    ((this.sizes && (this.sizes.length > 1 || this.product.is_clothing)) ||
                        (this.fabrics && this.fabrics.length > 0) ||
                        (this.modelings && this.modelings.length > 0))
                );
            },

            phoneSizesByModel() {
                if (!this.isPhone) return {};

                const modelSizes = {};

                this.sizes.forEach((variation) => {
                    const model = variation.size_name.split(' ')[0];

                    if (!modelSizes[model]) {
                        modelSizes[model] = [];
                    }

                    modelSizes[model].push(variation);
                });

                return modelSizes;
            },

            phoneModels() {
                return Object.keys(this.phoneSizesByModel).sort();
            },

            selectOptions() {
                if (this.isPhone) {
                    // natural sort
                    const collator = new Intl.Collator(undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    });

                    return this.selectedPhoneModel
                        ? this.phoneSizesByModel[this.selectedPhoneModel].sort((a, b) =>
                              collator.compare(a.size_name, b.size_name)
                          )
                        : [];
                }

                return this.sizes;
            },

            originalModelingsCount() {
                const searchObj = {};

                this.product.variations.forEach((v) => {
                    if (v.product_model_id && !searchObj[v.product_model_id]) {
                        searchObj[v.product_model_id] = true;
                    }
                });

                return Object.entries(searchObj).length;
            },

            hasDeliveryReadySizes() {
                return (
                    this.shopMode &&
                    this.product.in_stock &&
                    this.product.variations.some((v) => v.quantity > 0)
                );
            },

            variations() {
                const ignoreModelings = this.product.variations.filter(
                    (item) => item.product_model_id !== 1 && item.product_model_id !== 2
                );

                return this.reprintMode
                    ? ignoreModelings.filter(
                          (s) => (s.quantity === 0 && !s.virtual_stock) || !this.product.in_stock
                      )
                    : ignoreModelings.filter(
                          (v) =>
                              !this.product.is_clothing || !this.onlyDeliveryReady || v.quantity > 0
                      );
            },

            modelings() {
                const modelings = [];

                if (this.product.is_clothing) {
                    this.variations
                        .filter(
                            (v) =>
                                !!v.product_model_id &&
                                (!this.isMobile ||
                                    (this.currentFabric.fabric_id
                                        ? v.fabric_id === this.currentFabric.fabric_id
                                        : v.fabric_id === this.currentFabric.id))
                        )
                        .filter(
                            (v, i, array) =>
                                i ===
                                array.findIndex((v2) => v.product_model_id === v2.product_model_id)
                        )
                        .forEach((variation) =>
                            modelings.push({
                                id: variation.product_model_id,
                                name: variation.product_model_name,
                                url: variation.product_model_url,
                                gender: variation.gender,
                                has_any: this.variations.some(
                                    (v) =>
                                        v.product_model_id === variation.product_model_id &&
                                        this.variationHasStock(v) &&
                                        (!this.isMobile ||
                                            (this.currentFabric.fabric_id
                                                ? v.fabric_id === this.currentFabric.fabric_id
                                                : v.fabric_id === this.currentFabric.id))
                                ),
                            })
                        );
                }

                return modelings.sort((a, b) => {
                    return a.id > b.id ? -1 : 1;
                });
            },

            firstModeling() {
                return this.modelings ? this.modelings[0] : null;
            },

            // usada pra pegar a modelagem atualmente selecionada.
            // quando o usuário ainda não selecionou usamos a primeira
            currentModeling() {
                return this.selectedModeling || this.firstModeling;
            },

            sizes() {
                if (!this.product.is_clothing) {
                    return this.variations;
                }

                if (!this.currentModeling) return [];
                let sizes = this.variations
                    .filter(
                        (v) =>
                            this.isVariationAvailable(v) &&
                            this.currentModeling.id === v.product_model_id &&
                            (!this.isMobile ||
                                (this.currentFabric.fabric_id
                                    ? v.fabric_id === this.currentFabric.fabric_id
                                    : v.fabric_id === this.currentFabric.id))
                    )
                    .filter(
                        (v, i, array) => i === array.findIndex((v2) => v.size_id === v2.size_id)
                    )
                    .map((variation) => ({
                        size_id: variation.size_id,
                        size_name: variation.size_name,
                        gender: variation.gender,
                        has_any: this.variations.some(
                            (v) =>
                                v.product_model_id === this.currentModeling.id &&
                                v.size_id === variation.size_id &&
                                this.variationHasStock(v) &&
                                (!this.isMobile ||
                                    (this.currentFabric.fabric_id
                                        ? v.fabric_id === this.currentFabric.fabric_id
                                        : v.fabric_id === this.currentFabric.id))
                        ),
                    }));

                const sizesOrder = [
                    '2',
                    '4',
                    '6',
                    '8',
                    '10',
                    '12',
                    '14',
                    'PP',
                    'P',
                    'M',
                    'G',
                    'GG',
                    '2GG',
                    '3GG',
                    '4GG',
                ];

                sizes.sort((a, b) =>
                    sizesOrder.indexOf(a.size_name) > sizesOrder.indexOf(b.size_name) ? 1 : -1
                );

                return sizes;
            },

            firstSize() {
                return this.sizes ? this.sizes[0] : null;
            },

            // usada pra pegar o tamanho atualmente selecionada.
            // quando o usuário ainda não selecionou usamos a primeira
            currentSize() {
                return this.selectedSize || this.firstSize;
            },

            fabrics() {
                if (!this.isMobile && (!this.currentModeling || !this.currentSize)) return [];

                return this.variations
                    .filter(
                        (v) =>
                            v.fabric_id &&
                            (this.isMobile ||
                                (!this.isMobile &&
                                    v.product_model_id === this.currentModeling.id &&
                                    v.size_id === this.currentSize.size_id))
                    )
                    .filter(
                        (v, i, array) => i === array.findIndex((v2) => v.fabric_id === v2.fabric_id)
                    )
                    .map((variation) => ({
                        ...variation,
                        has_any: !this.isMobile
                            ? this.variations.some(
                                  (v) =>
                                      v.product_model_id === this.currentModeling.id &&
                                      v.fabric_id === variation.fabric_id &&
                                      (!this.selectedSize ||
                                          v.size_id === this.selectedSize.size_id) &&
                                      this.variationHasStock(v)
                              )
                            : this.variations.some(
                                  (v) =>
                                      v.fabric_id === variation.fabric_id &&
                                      this.variationHasStock(v)
                              ),
                    }))
                    .sort((a) => (a.fabric_id === this.product.fabric_id ? -1 : 1));
            },

            firstFabric() {
                return this.fabrics ? this.fabrics[0] : null;
            },

            // usada pra pegar a cor atualmente selecionada.
            // quando o usuário ainda não selecionou usamos a primeira
            currentFabric() {
                return this.selectedFabric || this.firstFabric;
            },
        },

        methods: {
            onResize() {
                this.isMobile = window.innerWidth < 768;
            },

            variationHasStock(size) {
                return (size.quantity > 0 || size.virtual_stock) && this.product.in_stock;
            },

            isVariationAvailable(variation) {
                return !this.onlyDeliveryReady || variation.quantity > 0;
            },

            updateSelectedVariation() {
                let equivalentSize = null;

                if (!this.product.is_clothing && this.selectedSize) {
                    equivalentSize = this.variations.find(
                        (v) => v.size_id === this.selectedSize.size_id
                    );

                    if (
                        !equivalentSize ||
                        (this.shopMode && !this.variationHasStock(equivalentSize))
                    ) {
                        equivalentSize = null;
                    }
                } else if (this.selectedSize && this.selectedModeling && this.selectedFabric) {
                    equivalentSize = this.variations.find(
                        (v) =>
                            v.product_model_id === this.selectedModeling.id &&
                            v.size_id === this.selectedSize.size_id &&
                            v.fabric_id === this.selectedFabric.fabric_id
                    );
                }

                this.$emit('input', equivalentSize);
            },

            setModeling(model) {
                this.selectedModeling = model;

                if (model && this.shopMode) {
                    EventBus.$emit('selected-modeling-change', model, this.product.id);
                }

                this.updateSelectedVariation();
            },

            setFabric(fabric) {
                this.selectedFabric = fabric;

                if (fabric && this.shopMode) {
                    EventBus.$emit('selected-color-change', fabric, this.product.id);
                }

                this.updateSelectedVariation();
            },

            setSize(size) {
                this.selectedSize = size;

                this.updateSelectedVariation();
            },

            onSizeClick(size) {
                this.setSize(size);
            },

            onFabricClick(fabric) {
                this.setFabric(fabric);
            },

            reportToTagManager() {
                this.loadingCart = true;
                let reportItems = [];
                let reportItemsV4 = [];

                const variation = !this.selectedVariation
                    ? this.product.variations.find(
                          (v) =>
                              (!this.selectedFabric ||
                                  (this.selectedFabric.id
                                      ? v.fabric_id === this.selectedFabric.id
                                      : v.fabric_id === this.selectedFabric.fabric_id)) &&
                              (!this.selectedModeling ||
                                  v.product_model_id === this.selectedModeling.id) &&
                              v.size_id === this.selectedSize.size_id
                      )
                    : this.selectedVariation;

                if (!this.$scope.IS_REGULAR_CUSTOMER) {
                    const variationInfo = this.buildEventVariationInfo(variation);
                    window.dataLayer.push(this.buildCartProductEvent(variationInfo));
                    reportItems.push(this.buildCartEventItem(variationInfo, 1));
                    reportItemsV4.push(this.buildCartEventItemV4(variationInfo, 1));
                } else {
                    const variationInfo = this.buildEventVariationInfo(variation);
                    window.dataLayer.push(this.buildCartProductEvent(variationInfo));
                    reportItems.push(this.buildCartEventItem(variationInfo));
                    reportItemsV4.push(this.buildCartEventItemV4(variationInfo));
                }

                window.dataLayer.push({
                    event: 'addToCart',
                    ecommerce: {
                        currencyCode: 'BRL',
                        add: { products: reportItems },
                    },
                });

                window.dataLayer.push({ ecommerce_v4: null });
                window.dataLayer.push({
                    event: 'add_to_cart',
                    ecommerce_v4: {
                        items: reportItemsV4,
                    },
                });
                this.loadingCart = false;
            },

            buildEventVariationInfo(variation) {
                const info = {};
                info.sizeName = variation.size_name === 'Único' ? 'Padrão' : variation.size_name;

                switch (variation.gender) {
                    case 'M':
                        info.genderName = 'Masculino';
                        break;
                    case 'F':
                        info.genderName = 'Feminino';
                        break;
                    case 'U':
                        info.genderName = 'Unisex';
                        break;
                    case 'I':
                        info.genderName = 'Infantil';
                        break;
                    default:
                        info.genderName = '';
                }

                info.colorName = variation.product_color_name;

                return info;
            },

            buildCartProductEvent(sizeInfo) {
                const event_id = 'ADTC_' + this.product.id;
                this.facebookEventId = event_id;
                return {
                    event: 'addToCartProduct',
                    'event-id-fb': event_id,
                    'product-size-name': sizeInfo.sizeName,
                    'product-gender-name': sizeInfo.genderName,
                };
            },

            buildCartEventItem(variationInfo, quantity = 1) {
                const reportProduct = {
                    name: this.product.name,
                    id: `${this.product.id}`,
                    price: `${this.price}`,
                    brand: 'Chico Rei',
                    category: this.category,
                    quantity,
                };

                reportProduct.variant = this.buildVariantReportItem(
                    variationInfo.genderName,
                    variationInfo.sizeName,
                    variationInfo.colorName
                );

                return reportProduct;
            },

            buildCartEventItemV4(variationInfo, quantity = 1) {
                const reportProductV4 = {
                    item_name: this.product.name,
                    item_id: `${this.product.id}`,
                    price: `${this.price}`,
                    item_brand: 'Chico Rei',
                    item_category: this.product.type.name,
                    item_category2: variationInfo.genderName,
                    item_category3: variationInfo.sizeName,
                    item_category4: variationInfo.colorName,
                    quantity: quantity,
                };

                reportProductV4.variant = this.buildVariantReportItem(
                    variationInfo.genderName,
                    variationInfo.sizeName,
                    variationInfo.colorName
                );

                return reportProductV4;
            },

            buildVariantReportItem(genderName, sizeName, colorName) {
                return `${genderName}-${sizeName}-${colorName}`
                    .replace('--', '-')
                    .replace(/-$/, '')
                    .replace(/^-/, '');
            },

            getCartBodyRequest() {
                const products = [];

                const variation = !this.selectedVariation
                    ? this.product.variations.find(
                          (v) =>
                              (!this.selectedFabric ||
                                  (this.selectedFabric.id
                                      ? v.fabric_id === this.selectedFabric.id
                                      : v.fabric_id === this.selectedFabric.fabric_id)) &&
                              (!this.selectedModeling ||
                                  v.product_model_id === this.selectedModeling.id) &&
                              v.size_id === this.selectedSize.size_id
                      )
                    : this.selectedVariation;

                products.push({
                    product_id: this.product.id,
                    variation_id: variation.variation_id,
                    quantity: 1,
                });

                const data = new FormData();
                data.append('json', '1');
                data.append('products', JSON.stringify(products));
                // data.append('fb_event_id', this.facebookEventId);
                return data;
            },

            addToCart(openSidebarCart = true, fromMobile = false) {
                if (fromMobile && !this.selectedVariation) {
                    this.sizeError = 'Selecione o tamanho para continuar';
                    return;
                }

                this.loadingCart = true;
                const cartBodyRequest = this.getCartBodyRequest();
                cartStore
                    .addProduct(cartBodyRequest, openSidebarCart)
                    .then((response) => {
                        this.$scope.cart_id = response.data.cart_id;
                        const products = JSON.parse(cartBodyRequest.get('products'));
                        const productId = products[0].product_id;
                        if (this.isMobile) {
                            EventBus.$emit('list-product-added-to-cart-mobile', productId);
                        }
                        this.productAddedToCart = {
                            text: 'Produto adicionado',
                            bg_color: '#000000',
                            status: true,
                        };
                    })
                    .catch(() => {
                        this.productAddedToCart = {
                            text: 'Erro ao adicionar',
                            bg_color: '#ff0000',
                            status: true,
                        };
                    })
                    .then(() => {
                        this.resetFields();
                        this.$emit('close');
                        this.loadingCart = false;
                        setTimeout(() => {
                            this.productAddedToCart.status = false;
                        }, 1500);
                        this.showAddToCart = true;
                    });
            },

            resetFields() {
                this.selectedModeling = null;
                this.selectedSize = null;
            },

            showEquivalentSize(variation) {
                this.selectedVariation = variation;
            },
        },
    };
</script>
